/**
 * Protected Route Component
 *
 * Wraps routes that require authentication
 * Redirects unauthenticated users to login
 */

import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ComponentType } from 'react';

interface ProtectedRouteProps {
  component: ComponentType;
  returnTo?: string;
}

/**
 * HOC to protect routes requiring authentication
 *
 * Usage:
 * <Route path="/foia-generator" element={<ProtectedRoute component={FOIAGenerator} />} />
 */
export function ProtectedRoute({ component, returnTo }: ProtectedRouteProps) {
  const Component = withAuthenticationRequired(component, {
    onRedirecting: () => (
      <div className="min-h-screen flex items-center justify-center bg-[#090E34]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F45F00] mx-auto mb-4"></div>
          <p className="text-[#FDFDE9] font-martianmono">Redirecting to login...</p>
        </div>
      </div>
    ),
    returnTo: returnTo || window.location.pathname
  });

  return <Component />;
}

/**
 * Simple loading component for auth state
 */
export function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#090E34]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F45F00] mx-auto mb-4"></div>
        <p className="text-[#FDFDE9] font-martianmono">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Error component for auth failures
 */
export function AuthError({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#090E34] p-4">
      <div className="max-w-md w-full bg-white rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-[#000050] mb-4">Authentication Error</h2>
        <p className="text-gray-700 mb-4">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-[#F45F00] text-white rounded-lg hover:bg-[#F45F00]/90 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}
